import _ from 'lodash';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import {
  toPaginate,
  getFiltersGlobalFilters,
  getBulkImportErrorData,
} from '@/utils/infrastructure';
import { VALIDATION_INTERNAL_ERRORS } from '@/utils/infrastructure/constants';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';

const api = bindServices('environments');
const { BulkActionType } = CoreEnums;

const namespace = 'environments';
const fetchRoute = 'environment';
const fetchParams = 'Environment';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams].paged.select,
  searchSelect: CoreQueries[fetchParams].paged.searchSelect,
  showForm: false,
  showPermissions: false,
  detail: false,
  id: null,
  item: null,
  permissionItem: null,
  success: false,
  validationErrors: [],
  showImport: false,
  bulkActionErrorData: null,
  showBulkActionErrors: false,
  // Required by useConfigListController (ConfigListPage) — must be a stable object, not missing.
  activatingItemIds: {},
  settingDefaultItemIds: {},
  userListPermission: null,
  positionListPermission: null,
  userGroupListPermission: null,
  areaListPermission: null,
  fetchingPermissionList: null,
  permissionsCache: {},
  restoreEnvironmentModal: {
    visible: false,
    message: '',
    environmentIds: null,
  },
};

const EnvironmentModel = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...CoreQueries[fetchParams].paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: { ...res, limit: payload.limit ?? pageSize, page: payload.page ?? 1 },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, { route: fetchRoute, data: payload.data });
        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        const validationErrors = e?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || e?.message;
          const environmentId = itemDeletedError.metadata.id;
          yield put({
            type: 'showRestoreEnvironmentModal',
            payload: { message, environmentId },
          });
          return;
        }
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, { route: fetchRoute, data: payload.data });
        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        const validationErrors = e?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || e?.message;
          const environmentId = itemDeletedError.metadata.id;
          yield put({
            type: 'showRestoreEnvironmentModal',
            payload: { message, environmentId },
          });
          return;
        }
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put, select }) {
      try {
        const { id, view, params } = payload;
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null },
        });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: {
              state: false,
              item: null,
              detail: false,
              id: null,
            },
          });
        }
        yield call(api.remove, { route: fetchRoute, id });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const pageSize = yield select((state) => state.global.pageSize);
        const envState = yield select((state) => state.environments);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const refetchParams = params || {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(envState.orderBy && { orderBy: envState.orderBy }),
          ...(filters && { filter: filters }),
          select: envState.select,
        };
        yield put({ type: 'fetchList', payload: refetchParams });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *restoreEnvironment({ payload }, { call, put, select }) {
      try {
        const { environmentId } = payload;
        const ids = Array.isArray(environmentId) ? environmentId : [environmentId];
        yield call(api.apiRoute, {
          route: `${fetchRoute}/restore`,
          method: 'post',
          data: ids,
        });
        yield put({ type: 'closeRestoreEnvironmentModal' });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, item: null, detail: false, id: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const pageSize = yield select((state) => state.global.pageSize);
        const envState = yield select((state) => state.environments);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const refetchParams = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(envState.orderBy && { orderBy: envState.orderBy }),
          ...(filters && { filter: filters }),
          select: envState.select,
        };
        yield put({ type: 'fetchList', payload: refetchParams });
      } catch (err) {
        if (err?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: err.message },
          });
        }
      }
    },

    *fetchActivate({ payload }, { put, call }) {
      const { id } = payload;
      yield put({ type: 'queryActivatingItem', payload: { id, active: true } });
      try {
        yield call(api.activate, {
          route: fetchRoute,
          id,
          isActive: payload.state,
        });
        yield put({ type: 'environmentActivate', payload });
      } catch (e) {
        /*  */
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { id, active: false } });
      }
    },

    *fetchSetDefault({ payload }, { put, call }) {
      const { id } = payload;
      yield put({ type: 'querySettingDefaultItem', payload: { id, active: true } });
      try {
        yield call(api.create, {
          route: `${fetchRoute}/default/${id}`,
          data: {},
        });
        yield put({ type: 'environmentSetDefault', payload });
      } catch (e) {
        /*  */
      } finally {
        yield put({ type: 'querySettingDefaultItem', payload: { id, active: false } });
      }
    },

    *bulkDeleteEnvironments({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const envState = yield select((state) => state.environments);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(envState.orderBy && { orderBy: envState.orderBy }),
          ...(filters && { filter: filters }),
          select: envState.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateEnvironments({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;

      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkEnvironmentActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *bulkDeactivateEnvironments({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;

      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkEnvironmentActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *showPermissions({ payload }, { put, call, select }) {
      yield put({ type: 'queryShowPermissions', payload });
      if (payload?.state !== false) return;
      yield call(() => new Promise((resolve) => setTimeout(resolve, 500)));
      const stillClosed = !(yield select((state) => state.environments.showPermissions));
      if (stillClosed) {
        yield put({ type: 'queryHidePermission' });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *setPaginationPage({ payload }, { put }) {
      yield put({ type: 'queryPaginationPage', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *toggleImportComponent({ payload }, { put }) {
      yield put({
        type: 'queryToggleImportComponent',
        payload: { showImport: payload.show },
      });
    },

    *fetchImport({ payload }, { call, put, select }) {
      try {
        const { file, isDeleteData } = payload;
        const pageSize = yield select((state) => state.global.pageSize);
        const envState = yield select((state) => state.environments);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(envState.orderBy && { orderBy: envState.orderBy }),
          ...(filters && { filter: filters }),
        };

        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import`,
          method: 'post',
          data: { fileContent: file, deleteExistingData: isDeleteData },
        });

        const errorData = getBulkImportErrorData(res, getIntl);
        const hasImportedRows = res.importedRowsCount && res.importedRowsCount > 0;

        if (errorData) {
          yield put({
            type: 'setBulkActionErrors',
            payload: { errorData },
          });
          yield put({
            type: 'toggleBulkActionErrorsDrawer',
            payload: { open: true },
          });
        }

        // Always close import modal
        yield put({ type: 'queryImport', payload: { ...res } });

        // Refresh list if any rows were imported successfully
        if (hasImportedRows) {
          yield put({ type: 'fetchList', payload: params });
        }
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *toggleBulkActionErrorsDrawer({ payload }, { put }) {
      yield put({
        type: 'queryToggleBulkActionErrorsDrawer',
        payload,
      });
    },

    *clearBulkActionErrors(_, { put }) {
      yield put({ type: 'queryClearBulkActionErrors' });
    },

    *fetchListPermissions({ payload }, { call, put, select }) {
      const { route, params, name, id } = payload;
      let trackingFetch = false;
      try {
        const currentState = yield select((state) => state.environments);

        const permissionTypeMap = {
          userListPermission: 'user',
          positionListPermission: 'position',
          userGroupListPermission: 'userGroup',
          areaListPermission: 'area',
        };
        const permissionType = permissionTypeMap[name] || name;
        const cacheKey = `${id}_${permissionType}`;

        if (currentState[name] !== null && currentState[name] !== undefined) {
          return;
        }

        if (currentState.permissionsCache[cacheKey] !== undefined) {
          yield put({
            type: 'queryListPermissions',
            payload: {
              [name]: currentState.permissionsCache[cacheKey],
            },
          });
          return;
        }

        trackingFetch = true;
        yield put({ type: 'queryFetchingPermissionList', payload: name });

        const data = yield call(api.getAll, { route, params });
        const processedData = (data || []).map((item) => ({
          ...item,
          name: item.name || item.title || item.fullName || item.displayName || item.description,
          fullName: item.fullName || item.name || item.title,
        }));

        yield put({
          type: 'cachePermissionsData',
          payload: { key: cacheKey, data: processedData },
        });

        yield put({
          type: 'queryListPermissions',
          payload: { [name]: processedData },
        });
      } catch (e) {
        /*  */
      } finally {
        if (trackingFetch) {
          yield put({ type: 'queryFetchingPermissionList', payload: null });
        }
      }
    },

    *savePermission({ payload }, { call, put, select }) {
      try {
        const { id } = payload;
        const currentState = yield select((state) => state.environments);

        const permissionConfigs = [
          {
            name: 'userListPermission',
            type: 'user',
            route: `environment/allowedUsers/${id}`,
            params: {
              select: 'id,fullName',
              expand: { position: { select: 'name' } },
              orderBy: 'fullName',
            },
          },
          {
            name: 'positionListPermission',
            type: 'position',
            route: `environment/allowedPositions/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
          {
            name: 'areaListPermission',
            type: 'area',
            route: `environment/allowedAreas/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
          {
            name: 'userGroupListPermission',
            type: 'userGroup',
            route: `environment/allowedUserGroups/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
        ];

        const permissionsCache = currentState.permissionsCache || {};
        const tabsToLoad = [];
        for (const config of permissionConfigs) {
          const cacheKey = `${id}_${config.type}`;
          if (permissionsCache[cacheKey] === undefined) {
            tabsToLoad.push(config);
          }
        }

        if (tabsToLoad.length > 0) {
          for (const config of tabsToLoad) {
            const cacheKey = `${id}_${config.type}`;
            const apiData = yield call(api.getAll, { route: config.route, params: config.params });
            const processedData = (apiData || []).map((item) => ({
              ...item,
              name:
                item.name || item.title || item.fullName || item.displayName || item.description,
              fullName: item.fullName || item.name || item.title,
            }));

            yield put({
              type: 'cachePermissionsData',
              payload: { key: cacheKey, data: processedData },
            });

            const currentLocalState = yield select((state) => state.environments[config.name]);
            if (
              currentLocalState === null ||
              (Array.isArray(currentLocalState) && currentLocalState.length === 0)
            ) {
              yield put({
                type: 'queryListPermissions',
                payload: { [config.name]: processedData },
              });
            }
          }
        }

        const updatedState = yield select((state) => state.environments);
        const saveData = {
          users: (updatedState.userListPermission || []).map((l) => ({ id: l.id })),
          positions: (updatedState.positionListPermission || []).map((l) => ({ id: l.id })),
          userGroups: (updatedState.userGroupListPermission || []).map((l) => ({ id: l.id })),
          areas: (updatedState.areaListPermission || []).map((l) => ({ id: l.id })),
        };

        yield call(api.create, {
          route: `environment/updatePermissions/${id}`,
          data: saveData,
        });

        const permissionLists = {
          userListPermission: 'user',
          positionListPermission: 'position',
          areaListPermission: 'area',
          userGroupListPermission: 'userGroup',
        };

        for (const listName of Object.keys(permissionLists)) {
          const permissionType = permissionLists[listName];
          const cacheKey = `${id}_${permissionType}`;
          const currentList = updatedState[listName] || [];
          const procData = currentList.map((item) => ({
            ...item,
            name: item.name || item.title || item.fullName || item.displayName || item.description,
          }));
          yield put({
            type: 'cachePermissionsData',
            payload: { key: cacheKey, data: procData },
          });
        }

        yield put({
          type: 'showPermissions',
          payload: { state: false, item: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *addListPermission({ payload }, { put }) {
      yield put({ type: 'queryAddListPermission', payload });
    },

    *deleteItemPermission({ payload }, { put }) {
      yield put({ type: 'queryDeleteItemPermision', payload });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    environmentActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },

    queryActivatingItem(state, { payload }) {
      const { id, ids, active } = payload || {};
      const activatingItemIds = { ...(state.activatingItemIds || {}) };

      if (Array.isArray(ids) && ids.length) {
        ids.forEach((itemId) => {
          if (itemId == null) return;
          if (active) {
            activatingItemIds[itemId] = true;
          } else {
            delete activatingItemIds[itemId];
          }
        });
        return { ...state, activatingItemIds };
      }

      if (id == null) {
        return { ...state, activatingItemIds: {} };
      }

      if (active) {
        activatingItemIds[id] = true;
      } else {
        delete activatingItemIds[id];
      }
      return { ...state, activatingItemIds };
    },

    querySettingDefaultItem(state, { payload }) {
      const { id, active } = payload || {};
      const settingDefaultItemIds = { ...(state.settingDefaultItemIds || {}) };

      if (id == null) {
        return { ...state, settingDefaultItemIds: {} };
      }

      if (active) {
        settingDefaultItemIds[id] = true;
      } else {
        delete settingDefaultItemIds[id];
      }
      return { ...state, settingDefaultItemIds };
    },

    environmentSetDefault(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isDefault: true } : { ...item, isDefault: false },
      );
      return { ...state, list };
    },

    bulkEnvironmentActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
      };
    },

    queryShowPermissions(state, { payload }) {
      return {
        ...state,
        showPermissions: payload.state,
        permissionItem: payload.item ?? null,
      };
    },

    editOrDetail(state, { payload }) {
      return {
        ...state,
        item: payload.item ?? {},
      };
    },

    queryOrderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryPaginationPage(state, { payload }) {
      return {
        ...state,
        pagination: { ...state.pagination, page: payload },
      };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    queryToggleImportComponent(state, { payload }) {
      return {
        ...state,
        showImport: payload.showImport ?? payload.show,
      };
    },

    queryImport(state, { payload }) {
      return {
        ...state,
        showImport: false,
      };
    },

    setBulkActionErrors(state, { payload }) {
      return {
        ...state,
        bulkActionErrorData: payload.errorData,
        showBulkActionErrors: true,
      };
    },

    queryToggleBulkActionErrorsDrawer(state, { payload }) {
      return {
        ...state,
        showBulkActionErrors: payload.open,
      };
    },

    queryClearBulkActionErrors(state) {
      return {
        ...state,
        bulkActionErrorData: null,
        showBulkActionErrors: false,
      };
    },

    queryListPermissions(state, { payload }) {
      return {
        ...state,
        userListPermission: payload.userListPermission ?? state.userListPermission,
        positionListPermission: payload.positionListPermission ?? state.positionListPermission,
        userGroupListPermission: payload.userGroupListPermission ?? state.userGroupListPermission,
        areaListPermission: payload.areaListPermission ?? state.areaListPermission,
      };
    },

    queryAddListPermission(state, { payload }) {
      return {
        ...state,
        [payload.name]: [...(state[payload.name] || []), payload.data],
      };
    },

    queryDeleteItemPermision(state, { payload }) {
      return {
        ...state,
        [payload.name]: _.filter(state[payload.name] || [], (l) => l.id !== payload.id),
      };
    },

    queryHidePermission(state) {
      return {
        ...state,
        showPermissions: false,
        id: null,
        permissionItem: null,
        userListPermission: null,
        positionListPermission: null,
        userGroupListPermission: null,
        areaListPermission: null,
        fetchingPermissionList: null,
        permissionsCache: {},
      };
    },

    queryFetchingPermissionList(state, { payload }) {
      return {
        ...state,
        fetchingPermissionList: payload ?? null,
      };
    },

    cachePermissionsData(state, { payload }) {
      const { key, data } = payload;
      return {
        ...state,
        permissionsCache: {
          ...state.permissionsCache,
          [key]: data,
        },
      };
    },

    showRestoreEnvironmentModal(state, { payload }) {
      const environmentIds =
        payload?.environmentIds ??
        (payload?.environmentId != null ? [payload.environmentId] : null);
      return {
        ...state,
        restoreEnvironmentModal: {
          visible: true,
          message: payload?.message || '',
          environmentIds,
        },
      };
    },

    closeRestoreEnvironmentModal(state) {
      return {
        ...state,
        restoreEnvironmentModal: {
          visible: false,
          message: '',
          environmentIds: null,
        },
      };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export default EnvironmentModel;
